import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Stack,
  Typography,
} from '@mui/material';
import { DialogClose } from '../../components/dialogs/DialogFrame/DialogClose';
import {
  IFacilityShareContentType,
  IFacilityShareTargetDraft,
} from '../../dto/FacilitySharing.dto';
import { IVisibleStation } from '../../dto/StationVisibility.dto';
import { useRqFacilityShare } from '../../react_query/facilitySharing/useRqFacilityShare';
import { useRqFacilityShareDetail } from '../../react_query/facilitySharing/useRqFacilityShareDetail';
import { useRqVisibleStations } from '../../react_query/stations/useRqVisibleStations';
import Toast from '../../package/Toaster';
import FacilitySharePreview from './FacilitySharePreview';
import FacilityShareFields, {
  buildFacilitySharePayloadFromDrafts,
  getDuplicateFacilityShareTargetStationIds,
  IFacilityShareStationOption,
  isFacilityShareTargetDraftValid,
  toFacilityShareDateTimeLocalValue,
} from './FacilityShareFields';

interface FacilityShareDialogProps {
  open: boolean;
  onClose: () => void;
  companyId: number;
  contentType: IFacilityShareContentType;
  contentId: number;
  title: string;
}

const FacilityShareDialog: React.FC<FacilityShareDialogProps> = ({
  open,
  onClose,
  companyId,
  contentType,
  contentId,
  title,
}) => {
  const [targets, setTargets] = useState<IFacilityShareTargetDraft[]>([]);

  const {data: shareDetail, isLoading: isLoadingShare} =
    useRqFacilityShareDetail(companyId, contentType, contentId, open);
  const {saveMutation, disableMutation} = useRqFacilityShare(
    companyId,
    contentType,
    contentId,
  );
  const {data: visibleStations} = useRqVisibleStations(open ? companyId : 0);

  const facilityStations = useMemo(() => {
    return (visibleStations || []).filter(
      (station: IVisibleStation) =>
        station.is_inherited &&
        station.visibility_reason === 'district_shared_facility',
    );
  }, [visibleStations]);

  const facilityStationOptions = useMemo<IFacilityShareStationOption[]>(() => {
    return facilityStations.map(station => ({
      id: station.id,
      name: station.name,
    }));
  }, [facilityStations]);

  const previewStations = useMemo(() => {
    const stationMap = new Map<number, IVisibleStation>();

    facilityStations.forEach(station => {
      stationMap.set(station.id, station);
    });

    targets.forEach(target => {
      if (target.target_station_id && !stationMap.has(target.target_station_id)) {
        stationMap.set(target.target_station_id, {
          id: target.target_station_id,
          name:
            target.target_station_name ||
            `Shared Facility #${target.target_station_id}`,
          company_id: companyId,
          is_inherited: true,
          editable: false,
          visibility_reason: 'district_shared_facility',
        });
      }
    });

    return Array.from(stationMap.values());
  }, [companyId, facilityStations, targets]);

  const hasActiveShare = Boolean(shareDetail?.is_active && shareDetail.targets.length > 0);

  useEffect(() => {
    if (!open) {
      return;
    }

    if (shareDetail?.is_active) {
      setTargets(
        shareDetail.targets.map(target => ({
          target_station_id: target.target_station_id,
          target_station_name: target.target_station_name,
          starts_at: toFacilityShareDateTimeLocalValue(target.starts_at),
          ends_at: toFacilityShareDateTimeLocalValue(target.ends_at),
        })),
      );

      return;
    }

    if (!isLoadingShare) {
      setTargets([]);
    }
  }, [isLoadingShare, open, shareDetail]);

  const duplicateTargetIds = useMemo(() => {
    return getDuplicateFacilityShareTargetStationIds(targets);
  }, [targets]);

  const canSave =
    targets.length > 0 &&
    duplicateTargetIds.length === 0 &&
    targets.every(isFacilityShareTargetDraftValid);

  const handleSave = useCallback(() => {
    if (!canSave) {
      return;
    }

    const payload = buildFacilitySharePayloadFromDrafts(targets);

    saveMutation.mutate(payload, {
      onSuccess: () => {
        Toast('Facility sharing updated successfully', 'success');
        onClose();
      },
    });
  }, [canSave, onClose, saveMutation, targets]);

  const handleDisableSharing = useCallback(() => {
    disableMutation.mutate(undefined, {
      onSuccess: () => {
        Toast('Facility sharing disabled', 'success');
        onClose();
      },
    });
  }, [disableMutation, onClose]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth='md'>
      <Stack
        direction='row'
        alignItems='center'
        justifyContent='space-between'
        px={3}
        pt={2}
        pb={1}
        sx={{borderBottom: '1px solid', borderColor: 'divider'}}>
        <Box>
          <Typography variant='h6'>Share to Shared Facility</Typography>
          <Typography variant='body2' color='text.secondary'>
            {title}
          </Typography>
        </Box>
        <DialogClose onClose={onClose} />
      </Stack>

      <DialogContent>
        <Stack spacing={2.5} mt={1}>
          <Alert severity='info'>
            Select the shared facility stations that should temporarily receive
            this content, then define the active window for each facility.
          </Alert>

          {shareDetail && !shareDetail.is_active ? (
            <Alert severity='warning'>
              Facility sharing is currently disabled for this content. Select
              stations and save to enable a new temporary share window.
            </Alert>
          ) : null}

          {!isLoadingShare && facilityStations.length === 0 ? (
            <Alert severity='warning'>
              No shared facility stations are currently visible to this company.
            </Alert>
          ) : null}

          <FacilityShareFields
            targets={targets}
            stationOptions={facilityStationOptions}
            onChange={setTargets}
            addButtonLabel='Add Shared Facility'
            emptyStateText='Choose one or more shared facility stations to configure access windows.'
          />

          <FacilitySharePreview
            selectedStationIds={targets.map(target => target.target_station_id)}
            facilityStations={previewStations}
            targetWindows={targets.reduce<Record<number, {starts_at: string; ends_at: string}>>(
              (acc, target) => {
                acc[target.target_station_id] = {
                  starts_at: target.starts_at,
                  ends_at: target.ends_at,
                };

                return acc;
              },
              {},
            )}
          />
        </Stack>
      </DialogContent>

      <DialogActions sx={{px: 3, pb: 3}}>
        {hasActiveShare ? (
          <Button
            variant='outlined'
            color='error'
            onClick={handleDisableSharing}
            disabled={disableMutation.isLoading}
            sx={{mr: 'auto'}}>
            {disableMutation.isLoading ? 'Disabling...' : 'Disable Sharing'}
          </Button>
        ) : null}
        <Button variant='outlined' onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant='contained'
          onClick={handleSave}
          disabled={!canSave || saveMutation.isLoading}>
          {saveMutation.isLoading ? (
            <CircularProgress size={20} color='inherit' />
          ) : (
            'Save Share'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FacilityShareDialog;